import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { FileSelector } from "components/base/Form";
import { Alert } from "components/base/Toast";
import useFileUploader from "hooks/useFileUploader";
import { useState } from "react";
import { BsTrash } from "react-icons/bs";
import { getOriginalFileName } from "utils/string";
import { SUPPLIER_CERTIFICATES } from "./const";
import { SAVE_SUPPLIER } from "./graphql";

function normalizeCertificates(certificates) {
  const initial = SUPPLIER_CERTIFICATES.reduce((acc, cert) => {
    acc[cert] = null;
    return acc;
  }, {});

  if (!certificates) return initial;

  if (Array.isArray(certificates)) {
    certificates.forEach((item) => {
      if (!item?.name || !SUPPLIER_CERTIFICATES.includes(item.name)) return;
      initial[item.name] = item.file_url || item.url || null;
    });
    return initial;
  }

  if (typeof certificates === "object") {
    SUPPLIER_CERTIFICATES.forEach((cert) => {
      initial[cert] = certificates[cert] || null;
    });
  }

  return initial;
}

const SupplierCertificatesForm = ({
  supplierId,
  originalCertificates,
  onSaved,
}) => {
  const [certificates, setCertificates] = useState(
    normalizeCertificates(originalCertificates),
  );

  function handleUploadedFiles(urls, context) {
    const type = context?.type;
    if (!type || urls.length === 0) return;

    setCertificates((prev) => ({
      ...prev,
      [type]: urls[0],
    }));
  }

  const { isUploading, handleFileUpload } = useFileUploader(
    handleUploadedFiles,
    "qiniu",
  );

  const [save, saveRes] = useMutation(SAVE_SUPPLIER, {
    onCompleted() {
      Alert("success", "Certificates updated successfully");
      onSaved?.();
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const disabled = saveRes.loading || isUploading;

  function saveHandler() {
    save({
      variables: {
        id: supplierId,
        certificates: JSON.stringify(certificates),
      },
    });
  }

  return (
    <div className="card">
      <h5>Certificates 证书</h5>
      <div className="mt-4 -mx-2 text-sm">
        <table>
          <tbody>
            {SUPPLIER_CERTIFICATES.map((cert) => (
              <tr
                key={cert}
                className="border-y border-gray-100 dark:border-gray-700"
              >
                <td className="align-middle">
                  <label>{cert}:</label>
                </td>
                <td className="w-full align-middle py-1 space-y-1">
                  <div className="flex justify-between">
                    <div>
                      {certificates[cert] ? (
                        <div className="flex items-center space-x-2">
                          <a
                            href={certificates[cert]}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-sm"
                          >
                            {getOriginalFileName(certificates[cert])}
                          </a>
                          <Button
                            disabled={disabled}
                            danger
                            onClick={() => {
                              setCertificates((prev) => ({
                                ...prev,
                                [cert]: null,
                              }));
                            }}
                          >
                            <BsTrash />
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    <FileSelector
                      disabled={disabled}
                      uploading={isUploading}
                      multiple={false}
                      title={
                        certificates[cert] ? "🔄 Replace File" : "↑ Upload File"
                      }
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        const files = [...e.target.files];
                        if (files.length === 0) return;
                        handleFileUpload(files, { type: cert });
                        e.target.value = null;
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <Button
          primary
          disabled={disabled}
          loading={saveRes.loading}
          onClick={saveHandler}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default SupplierCertificatesForm;
